import {
  AreaChart,
  Area,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import {
  TrendingUp,
  Eye,
  MousePointerClick,
  BarChart,
  ArrowUp,
  ArrowDown,
} from "lucide-react";
import { useTimeRange } from "../../../context/TimeRangeContext";

export default function CampaignStats({ statsData }) {
  const { timeRange } = useTimeRange();

  const getTrendData = (type) => {
    switch (type) {
      case "Increase":
        return {
          icon: <ArrowUp className="w-5 h-5" />,
          color: "text-green-500",
          stroke: "#22C55E",
          fill: "#22C55E",
        };
      case "Decrease":
        return {
          icon: <ArrowDown className="w-5 h-5" />,
          color: "text-red-500",
          stroke: "#EF4444",
          fill: "#EF4444",
        };
      default:
        return {
          icon: null, // No icon for "No change"
          color: "text-gray-500",
          stroke: "#9CA3AF",
          fill: "#9CA3AF",
        };
    }
  };

  const stats = [
    {
      label: "Delivered",
      value: statsData.delivered.total,
      change: statsData.delivered.change,
      icon: <TrendingUp className="w-6 h-6 text-gray-500" />,
      data: [{ value: 1800 }, { value: 1950 }, { value: 1700 }, { value: 2100 }, { value: statsData.delivered.total }],
    },
    {
      label: "Opened",
      value: statsData.opened.total,
      change: statsData.opened.change,
      icon: <Eye className="w-6 h-6 text-gray-500" />,
      data: [{ value: 1400 }, { value: 800 }, { value: 1050 }, { value: 2000 }, { value: statsData.opened.total }],
    },
    {
      label: "Clicked",
      value: statsData.clicked.total,
      change: statsData.clicked.change,
      icon: <MousePointerClick className="w-6 h-6 text-gray-500" />,
      data: [{ value: 200 }, { value: 280 }, { value: 240 }, { value: 290 }, { value: statsData.clicked.total }],
    },
    {
      label: "Bounced",
      value: statsData.bounced.total,
      change: statsData.bounced.change,
      icon: <BarChart className="w-6 h-6 text-gray-500" />,
      data: [{ value: 2100 }, { value: 2250 }, { value: 2150 }, { value: 2350 }, { value: statsData.bounced.total }],
    },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
      {stats.map(({ label, value, change, icon, data }) => {
        const { icon: TrendIcon, color, stroke, fill } = getTrendData(change?.type);

        return (
          <div
            key={label}
            className="flex flex-col justify-between p-6 gap-8 bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-200 dark:border-gray-800 transition-shadow"
          >
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">{label}</h3>
            <div className="flex items-center justify-between">
              <div className="grid gap-2">
                <p className="text-3xl font-bold text-gray-900 dark:text-white mt-1">
                  {value?.toLocaleString()}
                </p>

                <div className="flex items-center gap-1">
                  <div className={`flex items-center ${color}`}>
                    {TrendIcon && TrendIcon}
                    <span className="text-lg font-semibold">
                      {change?.percentage_change?.toFixed(1) ?? 0}%
                    </span>
                  </div>
                  <span className="text-gray-500 flex flex-col dark:text-gray-400 text-sm font-medium">
                    vs last <span>{timeRange}</span>
                  </span>
                </div>
              </div>

              {/* Mini Area Chart */}
              <div className="h-[100%] w-[60%]">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={data}>
                    <defs>
                      <linearGradient id={`color-${label}`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={fill} stopOpacity={0.4} />
                        <stop offset="95%" stopColor={fill} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <Tooltip contentStyle={{ display: "none" }} />
                    <Area
                      type="monotone"
                      dataKey="value"
                      stroke={stroke}
                      fillOpacity={1}
                      fill={`url(#color-${label})`}
                      strokeWidth={2.5}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
